// src/sections/notifications/normalizeNotification.js
// ─────────────────────────────────────────────────────────────────
// Maps backend notification JSON (verb, description, unread, etc.)
// to the prop shape expected by NotificationCard / NotificationPanel.
//
// Backend shape (from API):
//   { id, verb, level, description, action_link, action_object,
//     unread, timestamp, timestamp_human, data,
//     priority, requires_action, action_label, grouped_count,
//     snoozable, snoozed_until }
//
// Component shape (props):
//   { id, type, tier, message, timeAgo, preview, chips, practiceName,
//     starRating, isRead, priority, requires_action, action_label,
//     grouped_count, snoozable, timestamp, action_link }
// ─────────────────────────────────────────────────────────────────

import { getNotificationType } from './notificationTypes';

// ── verb → NOTIFICATION_TYPES key ───────────────────────────────

const VERB_TO_TYPE = {
  callback_request: 'CALLBACK_REQUEST',
  reminder_overdue: 'TASK_OVERDUE',
  lead_unassigned: 'LEAD_UNASSIGNED',
  reminder_due_today: 'TASK_DUE_TODAY',
  deposit_not_paid: 'DEPOSIT_NOT_PAID',
  new_lead: 'NEW_LEAD',
  lead_assigned_to_user: 'LEAD_ASSIGNED',
  reminder_assigned_to_user: 'TASK_ASSIGNED',
  new_email_reply_received: 'EMAIL_REPLY',
  facebook_post_comment_created: 'FACEBOOK_COMMENT',
  lead_restored: 'LEAD_RESTORED',
  whatsapp_message: 'WHATSAPP_MESSAGE',
  whatsapp_message_received: 'WHATSAPP_MESSAGE',
  deposit_paid: 'DEPOSIT_PAID',
  treatment_started: 'TREATMENT_STARTED',
  gmb_review_created: 'GOOGLE_REVIEW',
  phone_number_clicked: 'PHONE_CLICKED',
  // Mirror of phone_number_clicked — telemetry verb shipped from
  // analytics-events. Previously fell through the default arm and got
  // categorised as NEW_LEAD, which broke the icon, the "Comms" facet
  // filter, and the chip preset. PHONE_CLICKED is reused for both since
  // they share tier, action label, and behaviour.
  whatsapp_button_clicked: 'PHONE_CLICKED',
  lead_follow_up_moved: 'LEAD_FOLLOW_UP_MOVED',
};

export { VERB_TO_TYPE };

// ── verb → chips (source-based) ─────────────────────────────────

const VERB_CHIPS = {
  callback_request: [{ preset: 'call' }],
  new_lead: [{ preset: 'web' }],
  whatsapp_message: [{ preset: 'whatsapp' }],
  whatsapp_message_received: [{ preset: 'whatsapp' }],
  new_email_reply_received: [{ preset: 'email' }],
  facebook_post_comment_created: [{ preset: 'facebook' }],
  gmb_review_created: [{ preset: 'google' }],
  phone_number_clicked: [{ preset: 'call' }],
  whatsapp_button_clicked: [{ preset: 'whatsapp' }],
  reminder_overdue: [{ preset: 'overdue' }],
  reminder_due_today: [{ preset: 'overdue' }],
};

// ── Extract chips from action_object / verb ─────────────────────

function getChips(notification) {
  const chips = VERB_CHIPS[notification.verb] || [];

  // Add lead source chip if present
  const source = notification.action_object?.lead_source?.referral;
  if (source === 'facebook') return [{ preset: 'facebook' }, ...chips.filter((c) => c.preset !== 'web')];
  if (source === 'google') return [{ preset: 'google' }, ...chips.filter((c) => c.preset !== 'web')];

  // Add treatment chip if treatment data is present
  const treatment = notification.action_object?.treatment_name || notification.action_object?.treatment;
  if (treatment) return [...chips, { preset: 'treatment' }];

  return chips;
}

// ── Extract star rating for Google reviews ──────────────────────

function getStarRating(notification) {
  if (notification.verb !== 'gmb_review_created') return undefined;
  return notification.action_object?.star_rating
    ?? notification.action_object?.rating
    ?? undefined;
}

// ── Extract practice name ───────────────────────────────────────

function getPracticeName(notification) {
  return notification.action_object?.practice_name
    ?? notification.data?.practice_name
    ?? undefined;
}

// ── Extract preview text (for WhatsApp, email, reviews, FB) ─────

function getPreview(notification) {
  const config = getNotificationType(VERB_TO_TYPE[notification.verb] || 'NEW_LEAD');
  if (!config.hasPreview) return undefined;

  return notification.action_object?.preview
    ?? notification.action_object?.body
    ?? notification.action_object?.message
    ?? notification.action_object?.comment_message
    ?? notification.action_object?.review_text
    ?? undefined;
}

// ─────────────────────────────────────────────────────────────────
// normalizeNotification
// Maps a single backend notification object to the shape expected
// by NotificationCard, NotificationPanel, and NotificationList.
//
// @param {object} raw — backend notification JSON
// @returns {object}   — normalized notification for components
// ─────────────────────────────────────────────────────────────────

export default function normalizeNotification(raw) {
  const typeKey = VERB_TO_TYPE[raw.verb] || 'NEW_LEAD';
  const config = getNotificationType(typeKey);

  return {
    // ── Identity
    id: raw.id,

    // ── Card type / tier (for icon, colour, fallback actions)
    type: typeKey,
    tier: config.tier,

    // ── Content
    message: raw.description,
    timeAgo: raw.timestamp_human || '',
    preview: getPreview(raw),
    chips: getChips(raw),
    practiceName: getPracticeName(raw),
    starRating: getStarRating(raw),

    // ── Read state (backend sends `unread`, component expects `isRead`)
    isRead: !raw.unread,

    // ── 5 new backend fields (passed straight through)
    priority: raw.priority || 'normal',
    // Default to FALSE on missing — previously defaulted to TRUE which
    // meant any old/cached payload that didn't carry the field dumped
    // every notification into the Action tab. Bell shows it as an
    // Update if the BE forgot to send it; if you actually need it in
    // Action the BE serializer must set it explicitly. See
    // NOTIFICATION_VERB_REQUIRES_ACTION in dentalcrm/apps/practice/choices.py.
    requires_action: raw.requires_action ?? false,
    action_label: raw.action_label || undefined,
    grouped_count: raw.grouped_count ?? null,
    snoozable: raw.snoozable ?? false,
    snoozed_until: raw.snoozed_until ?? null,

    // ── Navigation / timestamp
    timestamp: raw.timestamp,
    action_link: raw.action_link,

    // ── Keep raw data for anything else consumers need
    _raw: raw,
  };
}

// ─────────────────────────────────────────────────────────────────
// normalizeNotifications  (batch helper)
// @param {Array} rawList — array of backend notification objects
// @returns {Array}       — array of normalized notifications
// ─────────────────────────────────────────────────────────────────

export function normalizeNotifications(rawList) {
  if (!Array.isArray(rawList)) return [];
  return rawList.map(normalizeNotification);
}
